'use client';

import { type ComponentPropsWithoutRef, useEffect, useRef } from 'react';
import { css, cx } from '@/styled-system/css';

const MOBILE_FLOATING_TOGGLE_Y_ADJUST_VAR = '--mobile-floating-toggle-y-adjust';

const floatingToggleBar = css({
  position: 'fixed',
  left: '50%',
  transform: 'translate(-50%, var(--mobile-floating-toggle-y-adjust, 0))',
  bottom: 'calc(env(safe-area-inset-bottom) + {spacing.default})',
  zIndex: 'zIndexOverParent',
  display: 'flex',
  maxWidth: 'calc(100vw - {spacing.loose})',
  borderRadius: 'pill',
  boxShadow: 'smHeavy',
  lg: {
    display: 'none',
  },
});

const floatingToggleBarAboveFooter = css({
  bottom: 'calc(env(safe-area-inset-bottom) + {sizes.action.md} + ({spacing.default} * 2) + {spacing.loose})',
});

type Props = ComponentPropsWithoutRef<'div'> & {
  placement?: 'aboveFooter' | 'default';
};

export function MobileFloatingToggleBar({ children, className, placement = 'default', ...props }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) return;

    let animationFrame = 0;

    const updateVisualViewportOffset = () => {
      animationFrame = 0;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      const currentAdjustment = parseFloat(wrapper.style.getPropertyValue(MOBILE_FLOATING_TOGGLE_Y_ADJUST_VAR)) || 0;
      const unadjustedBottom = wrapper.getBoundingClientRect().bottom - currentAdjustment;
      const unadjustedGap = visualViewport.height - unadjustedBottom;
      const desiredGap = parseFloat(window.getComputedStyle(wrapper).bottom) || 0;
      const nextAdjustment = Math.max(0, Math.ceil(unadjustedGap - desiredGap));

      wrapper.style.setProperty(MOBILE_FLOATING_TOGGLE_Y_ADJUST_VAR, nextAdjustment > 0 ? `${nextAdjustment}px` : '0');
    };

    const requestVisualViewportOffsetUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updateVisualViewportOffset);
    };

    requestVisualViewportOffsetUpdate();
    const initialSettleFrame = window.requestAnimationFrame(requestVisualViewportOffsetUpdate);
    visualViewport.addEventListener('resize', requestVisualViewportOffsetUpdate);
    visualViewport.addEventListener('scroll', requestVisualViewportOffsetUpdate);
    window.addEventListener('resize', requestVisualViewportOffsetUpdate);

    return () => {
      window.cancelAnimationFrame(initialSettleFrame);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      visualViewport.removeEventListener('resize', requestVisualViewportOffsetUpdate);
      visualViewport.removeEventListener('scroll', requestVisualViewportOffsetUpdate);
      window.removeEventListener('resize', requestVisualViewportOffsetUpdate);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={cx(floatingToggleBar, placement === 'aboveFooter' && floatingToggleBarAboveFooter, className)}
      {...props}
    >
      {children}
    </div>
  );
}
